/**
 * ASISTENTE DE REGULARIZACIÓN (F13.2) — activar el control de lotes de un artículo con historia
 *
 *   1. Explicación: el histórico NO se modifica.   2. Stock actual por almacén.
 *   3. Reparto del stock en lotes.                   4. Validación (suma EXACTA por almacén).
 *   5. Confirmación.                                 6. Resultado.
 *
 * Lo hace la base en una transacción (sólo ADMIN): por almacén, un ajuste con la
 * salida del saldo sin lote y la entrada del MISMO saldo en los lotes declarados.
 * Los lotes reales primero; el lote técnico LEGACY-AAAAMMDD sólo como último
 * recurso explícito. Las cantidades se comparan en enteros (micras) para que el
 * «cuadra» de la pantalla sea exactamente el de la base, sin coma flotante.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { api, type PreviaRegularizacion, type ResumenRegularizacion } from '../api.ts';
import { Error as ErrorCaja, Modal, Panel, useDatos } from '../componentes/base.tsx';
import { codigoAlmacen, codigoVisible } from '../componentes/codigo-articulo.tsx';

type Paso = 'explicacion' | 'stock' | 'reparto' | 'validacion' | 'confirmar' | 'resultado';
const PASOS: readonly (readonly [Paso, string])[] = [
  ['explicacion', 'Qué se hará'], ['stock', 'Stock actual'], ['reparto', 'Reparto en lotes'], ['validacion', 'Validación'], ['confirmar', 'Confirmar'], ['resultado', 'Resultado'],
];

interface LineaReparto { clave: number; codigoLote: string; tecnico: boolean; cantidad: string; fechaFabricacion: string; fechaCaducidad: string; proveedor: string }

/** Decimal en texto («12,5» o «12.5», hasta 6 decimales) → millonésimas; null si no es válido. */
export function aMicras(t: string): bigint | null {
  const v = t.trim().replace(',', '.');
  if (!/^\d{1,12}(\.\d{1,6})?$/.test(v)) return null;
  const [e, d = ''] = v.split('.');
  return BigInt(e!) * 1_000_000n + BigInt(d.padEnd(6, '0'));
}
export function deMicras(m: bigint): string {
  const s = m < 0n ? '-' : '';
  const a = m < 0n ? -m : m;
  const e = a / 1_000_000n;
  const d = (a % 1_000_000n).toString().padStart(6, '0').replace(/0+$/, '');
  return `${s}${e.toString()}${d === '' ? '' : `.${d}`}`;
}
let siguienteClave = 1;
const hoyCompacto = (): string => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Madrid' }).format(new Date()).replaceAll('-', '');

export function AsistenteRegularizacion({ idArticulo, cerrar, alTerminar }: { idArticulo: string; cerrar: () => void; alTerminar: () => void }): ReactNode {
  const previa = useDatos(() => api.get<PreviaRegularizacion>(`/api/inventario/articulos/${idArticulo}/regularizacion`), [idArticulo]);
  const [paso, setPaso] = useState<Paso>('explicacion');
  const [motivo, setMotivo] = useState('');
  const [caducidad, setCaducidad] = useState(false);
  const [reparto, setReparto] = useState<Record<string, LineaReparto[]>>({});
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<ResumenRegularizacion | null>(null);
  const p = previa.datos;
  const exigeCaducidad = p !== null && (p.articulo.controlaCaducidad || caducidad);
  const directa = p !== null && p.stock.length === 0;

  const nueva = (tecnico = false, cantidad = ''): LineaReparto =>
    ({ clave: siguienteClave++, codigoLote: '', tecnico, cantidad, fechaFabricacion: '', fechaCaducidad: '', proveedor: '' });
  const lineas = (alm: string): LineaReparto[] => reparto[alm] ?? [];
  const fijarLineas = (alm: string, l: LineaReparto[]): void => setReparto({ ...reparto, [alm]: l });
  const cambiar = (alm: string, i: number, cambio: Partial<LineaReparto>): void =>
    fijarLineas(alm, lineas(alm).map((x, k) => (k === i ? { ...x, ...cambio } : x)));

  //  Validación (paso 4): suma exacta por almacén, datos de cada línea y coherencia de un mismo lote.
  const validacion = useMemo(() => {
    if (p === null) return { porAlmacen: [], errores: [] as string[], ok: false };
    const errores: string[] = [];
    const porAlmacen = p.stock.map((s) => {
      let suma = 0n;
      let malas = 0;
      for (const l of lineas(s.almacen.id)) {
        const m = aMicras(l.cantidad);
        if (m === null || m <= 0n) malas++;
        else suma += m;
        if (!l.tecnico && l.codigoLote.trim() === '') malas++;
        if (exigeCaducidad && l.fechaCaducidad === '') malas++;
      }
      const stock = aMicras(s.saldo) ?? 0n;
      return { almacen: s.almacen, stock, suma, diferencia: stock - suma, malas, vacio: lineas(s.almacen.id).length === 0 };
    });
    for (const a of porAlmacen) {
      if (a.vacio) errores.push(`${codigoAlmacen(a.almacen)}: falta repartir su stock.`);
      else if (a.malas > 0) errores.push(`${codigoAlmacen(a.almacen)}: hay líneas sin código de lote, con cantidad no válida${exigeCaducidad ? ' o sin caducidad' : ''}.`);
      else if (a.diferencia !== 0n) errores.push(`${codigoAlmacen(a.almacen)}: el reparto suma ${deMicras(a.suma)} y el stock es ${deMicras(a.stock)} (${a.diferencia > 0n ? 'faltan' : 'sobran'} ${deMicras(a.diferencia > 0n ? a.diferencia : -a.diferencia)}).`);
    }
    const datos = new Map<string, string>();
    for (const ls of Object.values(reparto)) for (const l of ls) {
      const cod = (l.tecnico ? `LEGACY-${hoyCompacto()}` : l.codigoLote.trim()).toUpperCase();
      if (cod === '') continue;
      const firma = JSON.stringify([l.fechaFabricacion, l.fechaCaducidad, l.proveedor.trim()]);
      if (datos.has(cod) && datos.get(cod) !== firma) errores.push(`El lote ${cod} aparece con fechas o proveedor distintos.`);
      datos.set(cod, firma);
    }
    return { porAlmacen, errores: [...new Set(errores)], ok: errores.length === 0 };
  }, [p, reparto, exigeCaducidad]);

  const motivoOk = motivo.trim().length >= 5;
  async function regularizar(): Promise<void> {
    setError(null);
    setEnviando(true);
    try {
      const cuerpo = {
        motivo: motivo.trim(), activarCaducidad: caducidad,
        reparto: Object.entries(reparto).flatMap(([idAlmacen, ls]) => ls.map((l) => ({
          idAlmacen, cantidad: deMicras(aMicras(l.cantidad)!), ...(l.tecnico ? { tecnico: true } : { codigoLote: l.codigoLote.trim() }),
          ...(l.fechaFabricacion === '' ? {} : { fechaFabricacion: l.fechaFabricacion }), ...(l.fechaCaducidad === '' ? {} : { fechaCaducidad: l.fechaCaducidad }),
          ...(l.proveedor.trim() === '' ? {} : { proveedor: l.proveedor.trim() }),
        }))),
      };
      setResultado(await api.post<ResumenRegularizacion>(`/api/inventario/articulos/${idArticulo}/regularizacion`, cuerpo));
      setPaso('resultado');
      alTerminar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  const i = PASOS.findIndex(([k]) => k === paso);
  const visibles = directa ? PASOS.filter(([k]) => k !== 'reparto' && k !== 'validacion') : PASOS;
  const ir = (delta: number): void => {
    const j = visibles.findIndex(([k]) => k === paso) + delta;
    setPaso(visibles[j]![0]);
  };
  const puedeSeguir = paso === 'explicacion' ? p !== null && p.noAplica === null && p.bloqueos.length === 0 && motivoOk
    : paso === 'validacion' ? validacion.ok : true;

  return (
    <Modal titulo="Activar el control de lotes" cerrar={cerrar}
      pie={paso === 'resultado' ? <button type="button" className="principal" onClick={cerrar}>Cerrar</button> : (
        <>
          <button type="button" onClick={paso === 'explicacion' ? cerrar : () => ir(-1)} disabled={enviando}>{paso === 'explicacion' ? 'Cancelar' : 'Atrás'}</button>
          {paso === 'confirmar'
            ? <button type="button" className="principal" disabled={enviando} onClick={() => void regularizar()}>{enviando ? 'Regularizando…' : directa ? 'Activar lotes' : 'Regularizar y activar lotes'}</button>
            : <button type="button" className="principal" disabled={!puedeSeguir} onClick={() => ir(1)}>Siguiente</button>}
        </>
      )}>
      <div className="pasos" aria-label="Pasos de la regularización">
        {visibles.map(([k, t], n) => {
          const j = PASOS.findIndex(([x]) => x === k);
          return <div key={k} className={`paso ${j === i ? 'activo' : j < i ? 'hecho' : ''}`} aria-current={j === i ? 'step' : undefined}><span className="n">{j < i ? '✓' : n + 1}</span>{t}</div>;
        })}
      </div>
      {error !== null && <ErrorCaja error={error} />}
      <Panel estado={previa}>
        {(d) => (
          <>
            {paso === 'explicacion' && (
              <section aria-label="Qué se hará">
                <p><strong>{codigoVisible(d.articulo)}</strong> · {d.articulo.descripcion}</p>
                <p className="nota info" role="note">El histórico <strong>no se modifica</strong>: los movimientos anteriores se quedan tal cual, sin lote.
                  {d.stock.length === 0 ? ' Este artículo no tiene stock: el control de lotes se activa directamente.'
                    : ' Por cada almacén se registra un ajuste que da salida al stock actual sin lote y entrada a la MISMA cantidad repartida en los lotes que indiques. El stock total no cambia.'}
                  {' '}Desde ese momento, toda entrada, salida o consumo de este artículo exigirá lote.</p>
                {d.noAplica !== null && <p className="nota error" role="alert">{d.noAplica}</p>}
                {d.bloqueos.length > 0 && (
                  <div className="nota aviso" role="alert"><strong>Ahora no se puede regularizar:</strong>
                    <ul>{d.bloqueos.map((b) => <li key={b}>{b}</li>)}</ul></div>
                )}
                <label className="campo"><span>Motivo (queda en el registro)</span>
                  <textarea rows={2} maxLength={1000} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} placeholder="Por ejemplo: exigencia de trazabilidad del cliente" /></label>
                {!motivoOk && motivo !== '' && <p className="error-campo">Al menos 5 caracteres.</p>}
                {!d.articulo.controlaCaducidad && (
                  <label className="casilla"><input type="checkbox" checked={caducidad} onChange={(ev) => setCaducidad(ev.target.checked)} /> Activar también el control de caducidad (cada lote necesitará fecha de caducidad)</label>
                )}
              </section>
            )}
            {paso === 'stock' && (
              <section aria-label="Stock actual">
                {d.stock.length === 0 ? <p>Sin stock en ningún almacén: no hay nada que repartir.</p> : (
                  <div className="tabla-desliza">
                    <table aria-label="Stock actual por almacén">
                      <thead><tr><th>Almacén</th><th className="num">Stock sin lote</th></tr></thead>
                      <tbody>
                        {d.stock.map((s) => <tr key={s.almacen.id}><td>{codigoAlmacen(s.almacen)} · {s.almacen.descripcion}</td><td className="num">{s.saldo} {d.articulo.unidad.codigo}</td></tr>)}
                        <tr><td><strong>Total</strong></td><td className="num"><strong>{d.total} {d.articulo.unidad.codigo}</strong></td></tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
            {paso === 'reparto' && (
              <section aria-label="Reparto en lotes">
                <p className="suave pequena">Indica los lotes reales de las existencias (recuento físico o albarán del proveedor). Un mismo código en varios almacenes es el mismo lote.
                  El lote técnico sólo si de verdad no se conoce el lote.</p>
                {d.stock.map((s) => (
                  <fieldset key={s.almacen.id} className="panel" style={{ padding: 12, marginBottom: 12 }}>
                    <legend><strong>{codigoAlmacen(s.almacen)}</strong> · {s.saldo} {d.articulo.unidad.codigo}</legend>
                    {lineas(s.almacen.id).map((l, k) => (
                      <div key={l.clave} role="group" aria-label={`Lote ${String(k + 1)} de ${codigoAlmacen(s.almacen)}`}
                        style={{ borderTop: k === 0 ? undefined : '1px solid var(--borde)', paddingTop: k === 0 ? 0 : 10, marginBottom: 10 }}>
                      <div className="fila" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <strong className="pequena">Lote {k + 1}{l.tecnico ? ' · técnico' : ''}</strong>
                        <button type="button" onClick={() => fijarLineas(s.almacen.id, lineas(s.almacen.id).filter((_, x) => x !== k))}
                          aria-label={`Quitar el lote ${String(k + 1)} de ${codigoAlmacen(s.almacen)}`}>Quitar</button>
                      </div>
                      <div className="form-rejilla">
                        <label className="campo"><span>Código de lote</span>
                          <input maxLength={60} value={l.tecnico ? `LEGACY-${hoyCompacto()}` : l.codigoLote} disabled={l.tecnico}
                            onChange={(ev) => cambiar(s.almacen.id, k, { codigoLote: ev.target.value })} /></label>
                        <label className="campo"><span>Cantidad ({d.articulo.unidad.codigo})</span>
                          <input inputMode="decimal" value={l.cantidad} onChange={(ev) => cambiar(s.almacen.id, k, { cantidad: ev.target.value })} /></label>
                        <label className="campo"><span>Fabricación</span>
                          <input type="date" value={l.fechaFabricacion} onChange={(ev) => cambiar(s.almacen.id, k, { fechaFabricacion: ev.target.value })} /></label>
                        <label className="campo"><span>Caducidad{exigeCaducidad ? ' (obligatoria)' : ''}</span>
                          <input type="date" value={l.fechaCaducidad} onChange={(ev) => cambiar(s.almacen.id, k, { fechaCaducidad: ev.target.value })} /></label>
                        <label className="campo"><span>Proveedor</span>
                          <input maxLength={120} value={l.proveedor} onChange={(ev) => cambiar(s.almacen.id, k, { proveedor: ev.target.value })} /></label>
                      </div>
                        {l.tecnico && <p className="nota aviso pequena">Lote técnico: estas existencias quedarán identificadas como «sin lote real conocido».</p>}
                      </div>
                    ))}
                    <div className="fila" style={{ flexWrap: 'wrap', gap: 8 }}>
                      <button type="button" onClick={() => fijarLineas(s.almacen.id, [...lineas(s.almacen.id), nueva(false, lineas(s.almacen.id).length === 0 ? s.saldo : '')])}>Añadir lote</button>
                      <button type="button" onClick={() => fijarLineas(s.almacen.id, [...lineas(s.almacen.id), nueva(true, lineas(s.almacen.id).length === 0 ? s.saldo : '')])}
                        disabled={lineas(s.almacen.id).some((l) => l.tecnico)}>No conozco el lote: usar lote técnico</button>
                    </div>
                  </fieldset>
                ))}
              </section>
            )}
            {paso === 'validacion' && (
              <section aria-label="Validación">
                <div className="tabla-desliza">
                  <table aria-label="Cuadre por almacén">
                    <thead><tr><th>Almacén</th><th className="num">Stock</th><th className="num">Repartido</th><th>Cuadra</th></tr></thead>
                    <tbody>
                      {validacion.porAlmacen.map((a) => (
                        <tr key={a.almacen.id}><td>{codigoAlmacen(a.almacen)}</td><td className="num">{deMicras(a.stock)}</td><td className="num">{deMicras(a.suma)}</td>
                          <td>{a.diferencia === 0n && a.malas === 0 && !a.vacio ? <span className="etiqueta exito">Sí</span> : <span className="etiqueta error">No</span>}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {validacion.ok ? <p className="nota exito" role="status">El reparto cuadra exactamente con el stock de cada almacén.</p> : (
                  <div className="nota error" role="alert"><strong>No se puede continuar:</strong><ul>{validacion.errores.map((e) => <li key={e}>{e}</li>)}</ul></div>
                )}
              </section>
            )}
            {paso === 'confirmar' && (
              <section aria-label="Confirmar">
                <p>Se activará el control de lotes{caducidad ? ' y de caducidad' : ''} de <strong>{codigoVisible(d.articulo)}</strong>.</p>
                {!directa && (
                  <ul>
                    {Object.entries(reparto).flatMap(([alm, ls]) => ls.map((l) => (
                      <li key={`${alm}-${String(l.clave)}`}>{d.stock.find((s) => s.almacen.id === alm)?.almacen.codigo}: {deMicras(aMicras(l.cantidad) ?? 0n)} {d.articulo.unidad.codigo} → lote{' '}
                        <span className="mono">{l.tecnico ? `LEGACY-${hoyCompacto()}` : l.codigoLote.trim()}</span>{l.tecnico ? ' (técnico)' : ''}</li>
                    )))}
                  </ul>
                )}
                <p className="suave pequena">Motivo: {motivo.trim()}</p>
                <p className="nota aviso pequena">No se puede deshacer: después, el artículo siempre exigirá lote.</p>
              </section>
            )}
            {paso === 'resultado' && resultado !== null && (
              <section aria-label="Resultado">
                <p className="nota exito" role="status">Regularización <strong className="mono">{resultado.codigo}</strong> hecha: el artículo ya controla lotes.</p>
                {resultado.operaciones.length > 0 && (
                  <p>Ajustes: {resultado.operaciones.map((o, k) => <span key={o.id}>{k > 0 ? ', ' : ''}<Link to={`/inventario/operaciones/${o.id}`} className="mono">{o.codigo}</Link></span>)}</p>
                )}
                {resultado.reparto.length > 0 && (
                  <ul>{resultado.reparto.map((r, k) => (
                    <li key={k}>{codigoAlmacen(r.almacen)}: {r.cantidad} → {r.lote.id === null ? r.lote.codigo : <Link to={`/inventario/lotes/${r.lote.id}`} className="mono">{r.lote.codigo}</Link>}</li>
                  ))}</ul>
                )}
                <p className="suave pequena">Stock antes y después: {resultado.stockDespues.map((s) => `${codigoAlmacen(s.almacen)} ${s.saldo}`).join(' · ') || 'sin stock'}.</p>
              </section>
            )}
          </>
        )}
      </Panel>
    </Modal>
  );
}
